import {
    type ApiResult,
    type BaseParams,
    type EidCategories,
    type EmojiId,
    type ExtendedEmoji,
    links,
    type PostParams,
    Request,
    type ResourceId,
} from '@app/client';

import { apiReturnResults } from '../../ApiFactory';

export type UpdateFeaturesParams = BaseParams & PostParams<AdminEmojiIdFeatures> & { id: ResourceId };

type GetExtendedDataParams = BaseParams & { yat: EmojiId };

export interface AdminRegisterEmoji {
    eid: EmojiId;
    tags: EidCategories[];
    owner_pubkey: string;
}

export interface AdminUserEmojiList {
    user_id: string;
}

export interface AdminTransferEmoji {
    email?: string;
    pubkey?: string;
}

export interface AdminEmojiIdFeatures {
    blocked_until?: Date | null;
}

// Register
async function register(params: BaseParams & PostParams<AdminRegisterEmoji>): Promise<ApiResult<EmojiId>> {
    return Request.postRequest<EmojiId>({
        ...params,
        target: links.admin().emoji().collection(),
        settings: {
            ...params.settings,
        },
    });
}

// List
async function getCollection(params: BaseParams<AdminUserEmojiList>): Promise<ApiResult<EmojiId[]>> {
    return Request.getRequest<EmojiId[]>({
        ...params,
        target: links.admin().emoji().collection(),
    });
}

// Transfer
async function transfer(
    params: BaseParams & PostParams<AdminTransferEmoji> & { id: ResourceId },
): Promise<ApiResult<EmojiId>> {
    return Request.putRequest<EmojiId>({
        ...params,
        target: links.admin().emoji().transfer(params.id).single(),
        settings: {
            ...params.settings,
        },
    });
}

// Features
async function features(params: UpdateFeaturesParams): Promise<ApiResult<EmojiId>> {
    return Request.patchRequest<EmojiId>({
        ...params,
        target: links.admin().emoji().features(params.id).single(),
        settings: {
            ...params.settings,
        },
    });
}

// Delete
async function remove(params: BaseParams & { id: ResourceId }): Promise<ApiResult<EmojiId>> {
    return Request.deleteRequest<EmojiId>({
        ...params,
        target: links.admin().emoji().single(params.id),
        settings: {
            ...params.settings,
        },
    });
}

// Load extended data
async function adminLoad(params: GetExtendedDataParams): Promise<ApiResult<ExtendedEmoji>> {
    return Request.getRequest<ExtendedEmoji>({
        ...params,
        target: links.admin().emoji().single(params.yat),
        settings: {
            ...params.settings,
        },
    });
}

export const callUpdateEmojiFeatures = (params: UpdateFeaturesParams) => apiReturnResults<EmojiId>(features, params);

export const callGetExtendedData = (params: GetExtendedDataParams) =>
    apiReturnResults<ExtendedEmoji>(adminLoad, params);

export const emojiCalls = {
    register,
    getCollection,
    transfer,
    features,
    remove,
};
